import { Fragment, type ReactNode } from 'react';
import { chunkByOrder, useFoldedOrders } from '../../lib/orderGroups.js';
import { cn } from '@/lib/utils.js';

// ── כותרת הזמנה — אותה כותרת בכל מסך (ראה lib/orderGroups.ts) ──────────────
//
// שלוש צורות, כי המסכים שונים: שורת טבלה, פריט ברשת אריחים (לרוחב כל הרשת),
// וכותרת ברשימה או בטור. התוכן אחד — מספר ההזמנה, כמה סעיפים, וחץ שמקפל.

type Props = {
  base: string;
  count: number;
  folded: boolean;
  onToggle: () => void;
  /** מה שהמסך רוצה להוסיף — סכומים, „סמן הכל". */
  extra?: ReactNode;
};

function Inner({ base, count, folded, onToggle, extra }: Props): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }} aria-expanded={!folded}
        className="cursor-pointer inline-flex min-h-6 max-w-full flex-wrap items-center gap-x-1.5 rounded px-1 text-start font-bold text-navy-900 hover:bg-orange-100 focus-visible:outline-2 focus-visible:outline-brand-orange">
        <span aria-hidden="true">{folded ? '◂' : '▾'}</span>
        הזמנה <span className="font-mono" dir="ltr">{base}</span>
      </button>
      <span className="text-[12.5px] text-navy-600">{count} {count === 1 ? 'פרויקט' : 'פרויקטים'}{folded ? ' · מקופל' : ''}</span>
      {extra}
    </div>
  );
}

/** לרשימה או לטור: רק התוכן — המסך קובע את המסגרת. */
export const OrderHeaderInline = Inner;

const shell = 'bg-orange-50/70 border-orange-200';

export function OrderHeaderRow({ colSpan, ...p }: Props & { colSpan: number }): JSX.Element {
  return (
    <tr className={cn(shell, 'border-y-2')} data-testid={`order-${p.base}`}>
      <td colSpan={colSpan} className="px-2 py-1.5"><Inner {...p} /></td>
    </tr>
  );
}

/** לרשת אריחים: תופס שורה שלמה של הרשת. */
export function OrderHeaderBar(p: Props): JSX.Element {
  return (
    <div className={cn(shell, 'rounded-lg border-2 px-2 py-1.5')} style={{ gridColumn: '1 / -1' }} data-testid={`order-${p.base}`}>
      <Inner {...p} />
    </div>
  );
}

// ── עוטף אחד לכל רשימה: כותרת לכל הזמנה + השורות שלה, או מקופל ───────────────
//
// המסך מעביר את השורות כפי שהוא ממיין אותן ואת הדרך לצייר שורה אחת; העוטף
// מקבץ (lib/orderGroups.ts), מצייר כותרת במקום הנכון ומדלג על מה שקופל.
// ⚠ `children` מחזיר אלמנט עם `key` — כמו ב-map רגיל.
export function OrderChunks<T>({ rows, numberOf, screen, variant, colSpan = 1, extra, children }: {
  rows: T[];
  numberOf: (r: T) => string;
  /** לאיזה מסך נזכר הקיפול. */
  screen: string;
  /** row = שורת טבלה · bar = לרוחב רשת אריחים · block = כותרת עצמאית ברשימה. */
  variant: 'row' | 'bar' | 'block';
  colSpan?: number;
  extra?: (rows: T[]) => ReactNode;
  children: (r: T) => ReactNode;
}): JSX.Element {
  const { folded, toggle } = useFoldedOrders(screen);
  return (
    <>
      {chunkByOrder(rows, numberOf).map((c) => {
        const p: Props = {
          base: c.base, count: c.rows.length, folded: folded.has(c.base),
          onToggle: () => toggle(c.base), extra: extra?.(c.rows),
        };
        return (
          <Fragment key={c.base}>
            {c.family && (variant === 'row' ? <OrderHeaderRow colSpan={colSpan} {...p} />
              : variant === 'bar' ? <OrderHeaderBar {...p} />
                : <div className={cn(shell, 'rounded-lg border-2 px-2 py-1.5')} data-testid={`order-${c.base}`}><Inner {...p} /></div>)}
            {!(c.family && folded.has(c.base)) && c.rows.map((r) => children(r))}
          </Fragment>
        );
      })}
    </>
  );
}
